import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
} from '@/components/_common';
import { useMaximumNsu } from '@/core/domain/invoice/recipientInvoice/recipientInvoice.hooks';
import { useDesignatedNotes } from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.hooks';
import {
  ConsultDfe,
  DesignatedNotesFormValues,
} from '@/core/domain/invoiceTransmissionHub/AuxiliaryDocuments/AuxiliaryDocuments.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Col, Form, Input, Modal, ModalProps, Row, Select } from 'antd';
import { useEffect } from 'react';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
}

export default function RecipientInvoiceSearchModal({
  onCancel,
  ...props
}: Props) {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const [form] = Form.useForm<DesignatedNotesFormValues>();

  const { mutateAsync, isLoading } = useDesignatedNotes();
  const { data: numberMax, mutateAsync: getMaximumNsu } = useMaximumNsu();

  const companyId = Form.useWatch('companyId', form);
  const consultDFeEnum = Form.useWatch('consultDFeEnum', form);
  const nsuKey = Form.useWatch('nsuKey', form);

  useEffect(() => {
    if (companyId && consultDFeEnum === ConsultDfe.nsu) {
      getMaximumNsu({ companyId });
    }
  }, [companyId, consultDFeEnum]);

  useEffect(() => {
    if (numberMax) {
      form.setFieldValue('nsuKey', String(numberMax));
    }
  }, [numberMax]);

  const consultDfeOptions = [
    {
      value: ConsultDfe.nsu,
      label: 'NSU',
      min: 1,
      max: 15,
    },
    {
      value: ConsultDfe.nsuUnique,
      label: 'NSU Único',
      min: 1,
      max: 15,
    },
    {
      value: ConsultDfe.chave,
      label: 'Chave',
      min: 44,
      max: 44,
    },
  ];

  function handleCancel() {
    form.resetFields();

    onCancel();
  }

  async function handleCallback() {
    const values = await form.validateFields();

    await mutateAsync({
      chaveNsu: values.nsuKey,
      companyId: values.companyId,
      consultaDFeEnum: values.consultDFeEnum,
    });

    handleCancel();
  }

  const currentOption = consultDfeOptions.find(
    (item) => item.value === consultDFeEnum,
  );

  return (
    <Modal
      width={600}
      title="Pesquisar notas manifestadas"
      onCancel={handleCancel}
      onOk={handleCallback}
      okText="Pesquisar"
      confirmLoading={isLoading}
      {...props}
    >
      <Form form={form}>
        <Row gutter={[12, 12]}>
          <Col span={24} md={12}>
            <FloatingFormItem
              name="companyId"
              label="Empresa"
              rules={[defaultEmptyFieldError]}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12}>
            <FloatingFormItem
              name="consultDFeEnum"
              label="Consulta DFE"
              rules={[defaultEmptyFieldError]}
              getValueFromEvent={(value) => {
                if (!!nsuKey) form.validateFields(['nsuKey']);
                return value;
              }}
            >
              <Select
                options={consultDfeOptions.map((item) => ({
                  value: item.value,
                  label: item.label,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="nsuKey"
              label={currentOption?.label || 'Chave'}
              rules={
                !!currentOption
                  ? [
                      {
                        max: currentOption.max,
                        min: currentOption.min,
                        message:
                          currentOption.max === currentOption.min
                            ? `${currentOption.label} deve ter ${currentOption.max} dígitos`
                            : `${currentOption.label} deve ter entre ${currentOption.min} e ${currentOption.max} dígitos`,
                      },
                      defaultEmptyFieldError,
                    ]
                  : [defaultEmptyFieldError]
              }
            >
              <Input
                onChange={({ target: { value } }) =>
                  form.setFieldValue('nsuKey', value.trim())
                }
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
